/**
 * Transport for the unified customer map.
 *
 * The map fetches every site the user can see in one shot — no viewport
 * refetch — and does all filtering, searching and clustering on the client, so
 * panning and toggling filters never waits on the network. `detail=1` brings
 * the per-site loads, alarms and address the sidebar and hover card render.
 *
 * Contains:
 * - fetchCustomerMapSites — the whole visible site set for a customer, optionally narrowed
 */
import apiClient from '@sophocles/http-common';

// The endpoint is bounding-box shaped; the map asks for the whole world and
// clusters client-side. `zoom_level` only tells the backend how coarsely it may
// collapse points — 15 is past its clustering threshold, so it returns rows.
const WORLD_BOUNDS = Object.freeze({ min_lat: -90, max_lat: 90, min_lon: -180, max_lon: 180 });
const DETAIL_ZOOM_LEVEL = 15;

/**
 * Fetch the sites behind a customer's map.
 *
 * @param {object} query
 * @param {string|number} query.customerId - the customer whose sites to map.
 * @param {string|number} [query.siteGroupId] - narrow to one site group.
 * @param {string} [query.severity] - narrow to sites carrying an alarm at this severity.
 * @param {string[]} [query.loadTypes] - narrow to sites carrying a load of one of these
 *   relay load types; the same vocabulary the site grid filters on as `fixture_type`.
 * @param {object} [config] - axios config; threads an AbortSignal for stale-guarding.
 * @returns {Promise<{ sites: object[] }>}
 */
export const fetchCustomerMapSites = ({ customerId, siteGroupId, severity, loadTypes }, config) =>
  apiClient
    .get('/dashboard/map/', {
      params: {
        customer: customerId,
        ...(siteGroupId ? { site_group: siteGroupId } : {}),
        ...(severity ? { severity } : {}),
        ...(loadTypes?.length ? { load_type: loadTypes.join(',') } : {}),
        detail: 1,
        zoom_level: DETAIL_ZOOM_LEVEL,
        ...WORLD_BOUNDS,
      },
      ...config,
    })
    .then(res => res.data);
